import React, { memo, useMemo } from "react";

import { stripHtml } from "helpers";
import { CHANNEL_OPTIONS, ENTITY_TYPE_DEFAULT_TEMPLATES, ENTITY_TYPE_LABELS } from "services/api/notifications/consts";
import { NotificationScope } from "services/api/notifications/types";
import { ShowcaseIntro } from "ui/components/Showcase";
import { buildNotificationSchedule, formatScheduleDate, getExampleExpiryDate } from "ui/modules/notifications/schedule";

import { NOTIFICATION_INTRO_SAMPLE, NOTIFICATION_INTRO_TEXTS, NOTIFICATION_SAMPLE_ENTITY_NAMES } from "./consts";
import NotificationEntitiesScene from "./scenes/NotificationEntitiesScene";
import NotificationMessageScene from "./scenes/NotificationMessageScene";
import NotificationRecipientsScene from "./scenes/NotificationRecipientsScene";
import NotificationRulesScene from "./scenes/NotificationRulesScene";
import NotificationSummaryScene from "./scenes/NotificationSummaryScene";
import NotificationTypeScene from "./scenes/NotificationTypeScene";
import { NotificationIntroData, NotificationIntroProps, NotificationIntroScene, NotificationSceneProps } from "./types";

/** Scena za korak čarobnjaka notifikacije. */
const SCENES: Record<NotificationIntroScene, React.FC<NotificationSceneProps>> = {
  type: NotificationTypeScene,
  message: NotificationMessageScene,
  entities: NotificationEntitiesScene,
  recipients: NotificationRecipientsScene,
  rules: NotificationRulesScene,
  summary: NotificationSummaryScene,
};

/** Popuni oznake predloška primjerom, da poruka izgleda kao ona koja stvarno stiže. */
const fillTemplate = (template: string, entityName: string, expiryDate: string, days: number) =>
  stripHtml(
    template
      .replace(/\{\{\s*EntityName\s*\}\}/g, entityName)
      .replace(/\{\{\s*ExpiryDate\s*\}\}/g, expiryDate)
      .replace(/\{\{\s*DaysUntilExpiry\s*\}\}/g, String(days))
  );

/** Lijevi panel unosa notifikacije: za svaki korak vlastita scena s odabranim postavkama, a ispod objašnjenje. */
const NotificationIntro: React.FC<NotificationIntroProps> = ({ formValues, scene, isEdit, stepCurrent, stepCount }) => {
  const Scene = SCENES[scene];
  const text = NOTIFICATION_INTRO_TEXTS[isEdit ? "edit" : "create"][scene];

  const notification = useMemo<NotificationIntroData>(() => {
    const entityType = formValues.entityType ?? NOTIFICATION_INTRO_SAMPLE.entityType;
    const entityName = NOTIFICATION_SAMPLE_ENTITY_NAMES[entityType] ?? NOTIFICATION_SAMPLE_ENTITY_NAMES[NOTIFICATION_INTRO_SAMPLE.entityType];
    const days = formValues.daysBeforeExpiry ?? NOTIFICATION_INTRO_SAMPLE.daysBeforeExpiry;

    // Istek je izmišljen: stvarni se zna tek kad notifikacija dobije entitet.
    const exampleExpiry = getExampleExpiryDate();
    const expiryDate = formatScheduleDate(exampleExpiry);

    const hasChannels = formValues.channels > 0;
    const channels = CHANNEL_OPTIONS.map((option) => ({
      label: option.label,
      isActive: hasChannels ? !!(formValues.channels & option.value) : NOTIFICATION_INTRO_SAMPLE.channels.includes(option.label),
    }));

    const message = formValues.messageTemplate
      ? fillTemplate(formValues.messageTemplate, entityName, expiryDate, days)
      : NOTIFICATION_INTRO_SAMPLE.message;

    const schedule = buildNotificationSchedule(
      exampleExpiry,
      days,
      formValues.isRepeating ? formValues.repeatIntervalDays : null,
      formValues.isRepeating ? formValues.repeatCount : null
    ).map((item) => ({ date: formatScheduleDate(item.date), label: item.label }));

    const repeatText =
      formValues.isRepeating && formValues.repeatIntervalDays && formValues.repeatCount
        ? `Svakih ${formValues.repeatIntervalDays} dana, ${formValues.repeatCount} puta`
        : "Jednom, bez ponavljanja";

    return {
      entityType,
      entityLabel: ENTITY_TYPE_LABELS[entityType] ?? "",
      entityName,
      title: formValues.title || ENTITY_TYPE_DEFAULT_TEMPLATES[entityType]?.title || "",
      message,
      channels,
      activeChannelLabels: channels.filter((channel) => channel.isActive).map((channel) => channel.label),
      isCustom: formValues.isCustom,
      isIndividual: Number(formValues.scope) === NotificationScope.Individual && !formValues.isCustom,
      entityCount: formValues.includedEntityIds.length,
      roleCount: formValues.includedRoleIds.length,
      userCount: formValues.includedUserIds.length,
      excludedUserCount: formValues.excludedUserIds.length,
      daysBeforeExpiry: days,
      expiryDate,
      schedule,
      repeatText,
      notifyIfDateMissing: formValues.notifyIfDateMissing,
      customDate: formValues.customScheduledAt ? formatScheduleDate(new Date(formValues.customScheduledAt)) : expiryDate,
    };
  }, [formValues]);

  return (
    <ShowcaseIntro
      sceneKey={scene}
      title={text.title}
      description={text.description}
      stepCurrent={stepCurrent}
      stepCount={stepCount}
    >
      <Scene notification={notification} isEdit={isEdit} />
    </ShowcaseIntro>
  );
};

export default memo(NotificationIntro);
